import type { Store } from '@recipes/contracts';
import { haversineMeters, type LatLng, type OsmPlaceCandidate } from '@recipes/domain';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@recipes/ui';
import { useQuery } from '@tanstack/react-query';
import { LocateFixed, MapPin, Search } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyTastingMap } from '@/features/tasting/map/lazy-map';
import { PlacesLookupError, getPlacesClient } from '@/features/tasting/places';
import { toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { getPlatform } from '@/lib/platform';
import { createStoreLocal } from './catalog-queue';

const distance = (meters: number) =>
  meters < 1000 ? `${Math.round(meters / 10) * 10} m` : `${(meters / 1000).toFixed(1)} km`;

function CandidateList({
  candidates,
  origin,
  busyRef,
  onChoose,
}: {
  candidates: OsmPlaceCandidate[];
  origin: LatLng | null;
  busyRef: string | null;
  onChoose: (candidate: OsmPlaceCandidate) => void;
}) {
  const { t } = useTranslation();
  const ranked = candidates
    .map((c) => ({ c, d: origin ? haversineMeters(origin, c) : null }))
    .sort((a, b) => (a.d ?? 0) - (b.d ?? 0))
    .slice(0, 12);
  if (ranked.length === 0)
    return <p className="text-sm text-muted-foreground">{t('catalog.stores.noneNearby')}</p>;
  return (
    <ul className="divide-y rounded-md border" data-testid="store-candidates">
      {ranked.map(({ c, d }) => (
        <li key={c.ref}>
          <button
            type="button"
            disabled={busyRef !== null}
            onClick={() => onChoose(c)}
            className="flex min-h-[48px] w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent/40"
            data-testid="store-candidate"
          >
            <MapPin className="size-4 shrink-0 text-primary" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{c.name}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {[c.brand && c.brand !== c.name ? c.brand : null, c.street, c.city]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
            {d !== null && <span className="text-xs text-muted-foreground">{distance(d)}</span>}
            {busyRef === c.ref && <Spinner label={t('app.loading')} />}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Add a store (docs/finished/ingredient-catalog-spec.md 6.7): by name alone (a chain or any source),
 * or a branch picked from OpenStreetMap – the lookup goes from the device directly to the
 * configured OSM endpoints, with the same fair-use rules and disclosure as the restaurant
 * picker. A branch can also be pinned by hand.
 */
export function StoreDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (store: Store) => void;
}) {
  const { t } = useTranslation();
  const ids = { name: useId(), search: useId(), pinName: useId(), chain: useId() };
  const online = useOnlineStatus();
  const client = getPlacesClient();
  const [tab, setTab] = useState('search');
  const [name, setName] = useState('');
  const [position, setPosition] = useState<LatLng | null>(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [busyRef, setBusyRef] = useState<string | null>(null);
  const [pin, setPin] = useState<LatLng | null>(null);
  const [pinName, setPinName] = useState('');
  const [chain, setChain] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTab('search');
    setName('');
    setQuery('');
    setSubmitted('');
    setPin(null);
    setPinName('');
    setChain('');
  }, [open]);

  const locate = async () => {
    setLocating(true);
    setLocationError(false);
    try {
      const p = await (await getPlatform()).getCurrentPosition({ timeoutMs: 10_000 });
      setPosition({ lat: p.lat, lng: p.lng });
      setPin((prev) => prev ?? { lat: p.lat, lng: p.lng });
    } catch {
      setLocationError(true);
    } finally {
      setLocating(false);
    }
  };

  const nearby = useQuery({
    queryKey: ['catalog', 'stores', 'nearby', position?.lat.toFixed(4), position?.lng.toFixed(4)],
    queryFn: ({ signal }) => client.nearby(position!, { signal, kind: 'shops' }),
    enabled: open && tab === 'nearby' && client.enabled && online && position !== null,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60_000,
  });
  const search = useQuery({
    queryKey: ['catalog', 'stores', 'search', submitted, position?.lat.toFixed(2)],
    queryFn: ({ signal }) => client.search(submitted, { near: position, signal }),
    enabled: open && tab === 'search' && client.enabled && online && submitted.length >= 2,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60_000,
  });

  const finish = (store: Store) => {
    onCreated?.(store);
    onOpenChange(false);
  };

  const choose = async (candidate: OsmPlaceCandidate) => {
    setBusyRef(candidate.ref);
    try {
      finish(
        await createStoreLocal({
          kind: 'BRANCH',
          name: candidate.name,
          // The chain is taken from the brand/operator tag and matched by name on the server.
          chainName: candidate.brand ?? null,
          lat: candidate.lat,
          lng: candidate.lng,
          street: candidate.street,
          postalCode: candidate.postalCode,
          city: candidate.city,
          countryCode: candidate.countryCode,
          provider: 'OSM',
          providerRef: candidate.ref,
        }),
      );
    } catch (error) {
      toastError(error);
    } finally {
      setBusyRef(null);
    }
  };

  const lookupError = (error: unknown) => (
    <Alert variant="info" data-testid="stores-lookup-fallback">
      {error instanceof PlacesLookupError && error.reason === 'throttled'
        ? t('tasting.places.throttled')
        : t('tasting.places.unavailable')}
    </Alert>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('catalog.stores.add')}</DialogTitle>
          <DialogDescription>{t('catalog.stores.addHint')}</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          {/* Order as the user wants it (2026-09-24): search first, then nearby, name, pin. */}
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="search" data-testid="store-tab-search">
              {t('catalog.stores.tabSearch')}
            </TabsTrigger>
            <TabsTrigger value="nearby" data-testid="store-tab-nearby">
              {t('catalog.stores.tabNearby')}
            </TabsTrigger>
            <TabsTrigger value="name" data-testid="store-tab-name">
              {t('catalog.stores.tabName')}
            </TabsTrigger>
            <TabsTrigger value="pin" data-testid="store-tab-pin">
              {t('catalog.stores.tabPin')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="name">
            <form
              noValidate
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (!name.trim()) return;
                setSaving(true);
                createStoreLocal({ kind: 'CHAIN', name: name.trim() })
                  .then(finish)
                  .catch(toastError)
                  .finally(() => setSaving(false));
              }}
            >
              <div>
                <Label htmlFor={ids.name}>{t('catalog.stores.name')}</Label>
                <Input
                  id={ids.name}
                  className="mt-1"
                  maxLength={160}
                  value={name}
                  placeholder={t('catalog.stores.namePlaceholder')}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="store-name"
                />
                <p className="mt-1 text-xs text-muted-foreground">{t('catalog.stores.nameHint')}</p>
              </div>
              <Button
                type="submit"
                loading={saving}
                disabled={!name.trim()}
                data-testid="store-save"
              >
                {t('common.save')}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="nearby" className="space-y-3">
            <p className="text-xs text-muted-foreground">{t('catalog.stores.osmDisclosure')}</p>
            {!online ? (
              <Alert variant="info">{t('catalog.stores.offline')}</Alert>
            ) : position === null ? (
              <Button
                type="button"
                onClick={() => void locate()}
                loading={locating}
                data-testid="store-locate"
              >
                <LocateFixed aria-hidden /> {t('catalog.stores.useLocation')}
              </Button>
            ) : nearby.isLoading ? (
              <Spinner label={t('app.loading')} />
            ) : nearby.error ? (
              lookupError(nearby.error)
            ) : (
              <CandidateList
                candidates={nearby.data ?? []}
                origin={position}
                busyRef={busyRef}
                onChoose={(c) => void choose(c)}
              />
            )}
            {locationError && <Alert variant="warning">{t('catalog.stores.locationFailed')}</Alert>}
          </TabsContent>

          <TabsContent value="search" className="space-y-3">
            <p className="text-xs text-muted-foreground">{t('catalog.stores.osmDisclosure')}</p>
            <div role="search" className="flex items-end gap-2">
              <div className="flex-1">
                <Label htmlFor={ids.search}>{t('catalog.stores.searchLabel')}</Label>
                <Input
                  id={ids.search}
                  className="mt-1"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setSubmitted(query.trim());
                    }
                  }}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={!online}
                onClick={() => setSubmitted(query.trim())}
              >
                <Search aria-hidden /> {t('common.search')}
              </Button>
            </div>
            {search.isLoading && <Spinner label={t('app.loading')} />}
            {search.error ? lookupError(search.error) : null}
            {search.data && (
              <CandidateList
                candidates={search.data}
                origin={position}
                busyRef={busyRef}
                onChoose={(c) => void choose(c)}
              />
            )}
          </TabsContent>

          <TabsContent value="pin" className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor={ids.pinName}>{t('catalog.stores.name')}</Label>
                <Input
                  id={ids.pinName}
                  className="mt-1"
                  maxLength={160}
                  value={pinName}
                  onChange={(e) => setPinName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor={ids.chain}>{t('catalog.stores.chain')}</Label>
                <Input
                  id={ids.chain}
                  className="mt-1"
                  maxLength={120}
                  value={chain}
                  placeholder={t('common.optional')}
                  onChange={(e) => setChain(e.target.value)}
                />
              </div>
            </div>
            {pin === null ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => void locate()}
                loading={locating}
              >
                <LocateFixed aria-hidden /> {t('catalog.stores.useLocation')}
              </Button>
            ) : (
              <LazyTastingMap
                ariaLabel={t('catalog.stores.pinMap')}
                pin={pin}
                onPinChange={setPin}
                center={pin}
                zoom={16}
                className="h-56"
              />
            )}
            {locationError && <Alert variant="warning">{t('catalog.stores.locationFailed')}</Alert>}
            <Button
              type="button"
              loading={saving}
              disabled={!pinName.trim() || pin === null}
              onClick={() => {
                if (!pin) return;
                setSaving(true);
                createStoreLocal({
                  kind: 'BRANCH',
                  name: pinName.trim(),
                  chainName: chain.trim() || null,
                  lat: pin.lat,
                  lng: pin.lng,
                  provider: 'MANUAL',
                })
                  .then(finish)
                  .catch(toastError)
                  .finally(() => setSaving(false));
              }}
            >
              {t('common.save')}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
